import { createFileRoute } from "@tanstack/react-router";
import { Button } from "../../../components/core/basic/Button";
import { Flex } from "../../../../styled-system/jsx";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "../../../components/fields/base/TextField";
import { css } from "../../../../styled-system/css";
import { PageContainer } from "../../../components/core/basic/PageContainer";
import { Accordion } from "../../../components/core/basic/Accordion";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "../../../zodios/client";
import { useAppSelector } from "../../../redux/hooks";
import { errorToast, successToast } from "../../../utils/toasts";
import { useTranslation } from "react-i18next";

interface TRMNLFormData {
  email: string;
  deviceId: string;
  market_place: string;
}

const INITIAL_DATA: TRMNLFormData = {
  email: "",
  deviceId: "",
  market_place: "",
};
const containerStyle = css({
  width: "60%",
  marginX: "auto",
});

const listStyle = css({
  paddingLeft: "large",
});

const itemStyle = css({
  marginBottom: "medium",
  color: "darkblue",
  fontFamily: "poppins, sans-serif",
  fontSize: "large",
  lineHeight: "medium",
});

const titleStyle = css({
  display: "block",
  marginBottom: "xsmall",
  color: "royalPurple",
  fontFamily: "poppins, sans-serif",
  fontSize: "xlarge",
  fontWeight: "bold",
});

const Trmnl = () => {
  const { t } = useTranslation(["trmnl", "common", "validation"]);
  const { control, handleSubmit } = useForm({
    mode: "onBlur",
    defaultValues: INITIAL_DATA,
  });
  const market_place = useAppSelector((state) => state.marketPlace.marketPlace);

  const trmnlMutation = useMutation({
    mutationFn: async (data: TRMNLFormData) => {
      const { email, deviceId } = data;

      await apiClient.trmnluser({ email: email, id: deviceId, market_place: market_place }, {});
    },
    onSuccess: () => {
      successToast({ message: t(($) => $.trmnl["TRMNL device setup successful!"]) });
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.trmnl["TRMNL device setup failed!"]), error });
    },
  });

  const onSubmit = (data: TRMNLFormData) => {
    trmnlMutation.mutate({ email: data.email, deviceId: data.deviceId, market_place: market_place });
  };

  return (
    <PageContainer>
      <h2 className={css({ marginBottom: "1rem", textAlign: "center" })}>{t(($) => $.trmnl["TRMNL Device Setup"])}</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex
          className={css({ marginTop: "2rem", padding: "1rem" })}
          gap={"medium"}
          flexDirection={"column"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Controller
            name={"email"}
            control={control}
            rules={{
              required: { value: true, message: t(($) => $.validation.email["An email is required"]) },
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t(($) => $.validation.email["Invalid email address"]),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={t(($) => $.trmnl["Email"])}
                type={"email"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
                placeholder={t(($) => $.trmnl["Enter your email ID"])}
                css={{ width: "40%" }}
              />
            )}
          />
          <Controller
            name={"deviceId"}
            control={control}
            rules={{
              required: { value: true, message: t(($) => $.trmnl["A device ID(Friendly ID) is required"]) },
            }}
            render={({ field, fieldState }) => (
              <TextField
                label={t(($) => $.trmnl["Device ID(Friendly ID)"])}
                type={"text"}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
                placeholder={t(($) => $.trmnl["Enter your device ID (Friendly ID)"])}
                css={{ width: "40%" }}
              />
            )}
          />
          <Button isPending={trmnlMutation.isPending} type="submit">
            {t(($) => $.common.actions.Submit)}
          </Button>
        </Flex>
      </form>
      <Accordion title={t(($) => $.trmnl["How to connect your TRMNL to Wi-Fi?"])} defaultExpanded={true}>
        <h5 className={css({ fontFamily: "poppins, sans-serif" })}>
          {t(($) => $.trmnl["Connecting Your TRMNL to Wi-Fi and find your device ID(Friendly ID)"])}
        </h5>
        <Flex className={css({ margin: "1rem" })} gap={"medium"} flexDirection={"row"} justifyContent={"space-between"}>
          <div className={containerStyle}>
            <ol className={listStyle}>
              <li className={itemStyle}>
                <span className={titleStyle}>{t(($) => $.trmnl["Power on your TRMNL"])}</span>
                {t(
                  ($) =>
                    $.trmnl[
                      "Flip the power switch to ON on the back of your TRMNL device. If the device doesn't turn on, the battery may be drained. Plug it in using a USB-C cable and let it charge for a few minutes and come back."
                    ],
                )}
              </li>
              <li className={itemStyle}>
                <span className={titleStyle}>{t(($) => $.trmnl["Connect to the TRMNL Wi-Fi network"])}</span>
                {t(
                  ($) =>
                    $.trmnl[
                      'On your phone or a nearby computer, open your Wi-Fi settings and connect to the wi-fi network named "TRMNL". Wait a few seconds for the network to appear.'
                    ],
                )}
              </li>
              <li className={itemStyle}>
                <span className={titleStyle}>{t(($) => $.trmnl["Access the TRMNL setup portal"])}</span>
                {t(($) => $.trmnl["Once connected, a setup page (Wi-Fi portal) will open automatically."])}
              </li>
              <li className={itemStyle}>
                <span className={titleStyle}>{t(($) => $.trmnl["Choose or enter your network"])}</span>
                {t(
                  ($) =>
                    $.trmnl[
                      "Wait a moment while TRMNL scans for available networks. If your home or office network doesn't appear, or is hidden, manually enter the SSID and Password in the fields provided."
                    ],
                )}
              </li>
              <li className={itemStyle}>
                <span className={titleStyle}>{t(($) => $.trmnl["Finalize setup"])}</span>
                {t(
                  ($) =>
                    $.trmnl[
                      "After your Wi-Fi details are saved, TRMNL will restart and display a setup message. This will include your Friendly ID. Note it down as you'll need it for the next step."
                    ],
                )}
              </li>
              <li className={itemStyle}>
                <span className={titleStyle}>{t(($) => $.trmnl["Enter your email and Friendly ID"])}</span>
                {t(
                  ($) =>
                    $.trmnl[
                      "After your Friendly ID appears scroll to the top of this page and enter your email and Friendly ID in the provided text fields then click submit."
                    ],
                )}
              </li>
            </ol>
          </div>

          <div className={css({ marginLeft: "1rem" })}>
            <iframe
              sandbox=""
              width="600"
              height="315"
              src="https://www.youtube.com/embed/AR3-UX2_UNc"
              title={t(($) => $.trmnl["YouTube video player"])}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className={css({ borderRadius: "8px", marginRight: "1rem" })}
              loading="lazy"
            />
            <p className={css({ marginTop: "0.5rem", color: "#2E0054", fontFamily: "poppins, sans-serif" })}>
              {t(($) => $.trmnl["For more information, check the video above."])}
            </p>
          </div>
        </Flex>
      </Accordion>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_authenticated/user/trmnl")({
  component: Trmnl,
  staticData: { application: "Admin" },
});
